import { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Checkbox } from "primereact/checkbox";
import {
  musicClassifierApi,
  type MusicClassifierRunDocument,
} from "../../api/musicClassifierApi";

const BUTTON_TOOLTIP_OPTIONS = { position: "top" as const };

const COLUMN_IDS = [
  "date",
  "createdBy",
  "channelTitle",
  "channelSubscriberCount",
  "videoCount",
  "pctMusical",
  "totalViews",
  "pctMusicalViews",
  "channelInMusicCms",
  "madeForKids",
  "runId",
  "channelId",
  "actions",
] as const;

const DEFAULT_VISIBLE: Record<(typeof COLUMN_IDS)[number], boolean> = {
  date: true,
  createdBy: true,
  channelTitle: true,
  channelSubscriberCount: true,
  videoCount: true,
  pctMusical: true,
  totalViews: true,
  pctMusicalViews: true,
  channelInMusicCms: true,
  madeForKids: true,
  runId: false,
  channelId: false,
  actions: true,
};

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function videoCount(run: MusicClassifierRunDocument): number {
  if (run.truncatedResults && run.totalResultCount != null)
    return run.totalResultCount;
  return run.results?.length ?? 0;
}

function runViewStats(run: MusicClassifierRunDocument): {
  totalViews: number;
  musicViews: number;
  pctViewsFromMusic: number | null;
} {
  const results = run.results ?? [];
  const totalViews = results.reduce((sum, r) => sum + (r.views ?? 0), 0);
  const musicViews = results.reduce(
    (sum, r) =>
      sum + (r.is_musical_opportunity === true ? (r.views ?? 0) : 0),
    0
  );
  const pctViewsFromMusic =
    totalViews > 0 ? Math.round((musicViews / totalViews) * 100) : null;
  return { totalViews, musicViews, pctViewsFromMusic };
}

function runPctMusical(run: MusicClassifierRunDocument): number | null {
  const n = videoCount(run);
  if (n === 0) return null;
  const results = run.results ?? [];
  const musicCount = results.filter((r) => r.is_musical_opportunity === true).length;
  return Math.round((musicCount / n) * 100);
}

const MusicClassifierHistoryTab = () => {
  const [runs, setRuns] = useState<MusicClassifierRunDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState("");
  const [visibleColumns, setVisibleColumns] =
    useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const toast = useRef<Toast>(null);
  const columnsOpRef = useRef<OverlayPanel>(null);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const { runs: list } = await musicClassifierApi.listRuns({
        limit: 100,
        channelId: channelFilter.trim() || undefined,
      });
      setRuns(list ?? []);
    } catch (e: unknown) {
      setRuns([]);
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : (e as Error)?.message;
      toast.current?.show({
        severity: "error",
        summary: "Failed to load runs",
        detail: String(msg),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const onFilter = () => loadRuns();

  const toggleColumn = (id: (typeof COLUMN_IDS)[number]) => {
    setVisibleColumns((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const col = (id: (typeof COLUMN_IDS)[number]) => visibleColumns[id] !== false;

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <h3 className="mt-0">Music Classifier History</h3>
      <p className="text-color-secondary mb-4">
        View all classification runs and download CSV exports with full video analysis.
      </p>

      <div className="flex flex-wrap align-items-center gap-2 mb-4">
        <label htmlFor="channelFilter" className="sr-only">
          Channel ID filter
        </label>
        <InputText
          id="channelFilter"
          placeholder="Filter by channel ID (optional)"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onFilter()}
          className="w-20rem"
        />
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          loading={loading}
          onClick={loadRuns}
          severity="secondary"
        />
        <Button label="Filter" icon="pi pi-filter" onClick={onFilter} />
        <Button
          label="Columns"
          icon="pi pi-list"
          onClick={(e) => columnsOpRef.current?.toggle(e)}
          severity="secondary"
          aria-haspopup
        />
        <OverlayPanel ref={columnsOpRef} appendTo={typeof document !== "undefined" ? document.body : undefined}>
          <div className="flex flex-column gap-2">
            <strong className="block mb-2">Show columns</strong>
            {COLUMN_IDS.filter((id) => id !== "actions").map((id) => (
              <div key={id} className="flex align-items-center gap-2">
                <Checkbox
                  inputId={`col-${id}`}
                  checked={visibleColumns[id] !== false}
                  onChange={() => toggleColumn(id)}
                />
                <label htmlFor={`col-${id}`} className="cursor-pointer">
                  {id === "date" && "Date"}
                  {id === "createdBy" && "User"}
                  {id === "channelTitle" && "Channel title"}
                  {id === "channelSubscriberCount" && "Subscribers"}
                  {id === "videoCount" && "Video count"}
                  {id === "pctMusical" && "% musical"}
                  {id === "totalViews" && "Views"}
                  {id === "pctMusicalViews" && "% musical views"}
                  {id === "channelInMusicCms" && "Music CMS"}
                  {id === "madeForKids" && "MFK"}
                  {id === "runId" && "Run ID"}
                  {id === "channelId" && "Channel ID"}
                </label>
              </div>
            ))}
          </div>
        </OverlayPanel>
      </div>

      <DataTable
        value={runs}
        loading={loading}
        paginator
        rows={15}
        rowsPerPageOptions={[10, 15, 25, 50]}
        emptyMessage="No runs found."
        className="mt-2"
      >
        {col("date") && (
          <Column
            field="dateCreated"
            header="Date"
            sortable
            body={(row: MusicClassifierRunDocument) => formatDate(row.dateCreated)}
          />
        )}
        {col("createdBy") && (
          <Column
            field="createdBy"
            header="User"
            body={(row: MusicClassifierRunDocument) =>
              row.createdBy ?? "—"
            }
          />
        )}
        {col("channelTitle") && (
          <Column
            header="Channel title"
            body={(row: MusicClassifierRunDocument) =>
              row.channelTitle ?? "—"
            }
          />
        )}
        {col("channelSubscriberCount") && (
          <Column
            header="Subscribers"
            field="channelSubscriberCount"
            sortable
            body={(row: MusicClassifierRunDocument) =>
              row.channelSubscriberCount != null
                ? row.channelSubscriberCount.toLocaleString()
                : "—"
            }
          />
        )}
        {col("videoCount") && (
          <Column
            header="Video count"
            body={(row: MusicClassifierRunDocument) => {
              const n = videoCount(row);
              const truncated = row.truncatedResults && row.totalResultCount != null;
              return truncated ? (
                <span title={`First ${row.results?.length ?? 0} of ${n} stored`}>
                  {n} (truncated)
                </span>
              ) : (
                n
              );
            }}
          />
        )}
        {col("pctMusical") && (
          <Column
            header="% musical"
            body={(row: MusicClassifierRunDocument) => {
              const pct = runPctMusical(row);
              return pct != null ? `${pct}%` : "—";
            }}
          />
        )}
        {col("totalViews") && (
          <Column
            header="Views"
            body={(row: MusicClassifierRunDocument) => {
              const { totalViews } = runViewStats(row);
              return totalViews > 0 ? totalViews.toLocaleString() : "—";
            }}
          />
        )}
        {col("pctMusicalViews") && (
          <Column
            header="% musical views"
            body={(row: MusicClassifierRunDocument) => {
              const { pctViewsFromMusic } = runViewStats(row);
              return pctViewsFromMusic != null
                ? `${pctViewsFromMusic}%`
                : "—";
            }}
          />
        )}
        {col("channelInMusicCms") && (
          <Column
            header="Music CMS"
            body={(row: MusicClassifierRunDocument) =>
              row.channelInMusicCms === true
                ? "Yes"
                : row.channelInMusicCms === false
                  ? "No"
                  : "—"
            }
          />
        )}
        {col("madeForKids") && (
          <Column
            header="MFK"
            body={(row: MusicClassifierRunDocument) =>
              row.channelMadeForKids === true
                ? "Yes"
                : row.channelMadeForKids === false
                  ? "No"
                  : "—"
            }
          />
        )}
        {col("runId") && (
          <Column field="runId" header="Run ID" sortable />
        )}
        {col("channelId") && (
          <Column field="channelId" header="Channel ID" />
        )}
        {col("actions") && (
          <Column
            header="Actions"
            body={(row: MusicClassifierRunDocument) => (
              <div className="flex gap-2 flex-wrap">
                <Button
                  label="Download CSV"
                  icon="pi pi-download"
                  size="small"
                  severity="secondary"
                  onClick={() => musicClassifierApi.downloadExport(row.runId)}
                  tooltip="Full CSV with all columns including description"
                  tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
                />
                <Button
                  label="Compact CSV"
                  icon="pi pi-download"
                  size="small"
                  outlined
                  onClick={() =>
                    musicClassifierApi.downloadExport(row.runId, { compact: true })
                  }
                  tooltip="CSV without description column"
                  tooltipOptions={BUTTON_TOOLTIP_OPTIONS}
                />
              </div>
            )}
          />
        )}
      </DataTable>
    </div>
  );
};

export default MusicClassifierHistoryTab;
